import React, { useEffect, useRef, useState } from 'react';
import { inSaudiArabia, SA_BOUNDS } from '../../lib/saAddress';

// Pick the delivery point on Google Maps: search a place (Places Autocomplete,
// Saudi Arabia only), drop / drag the pin onto the building, and the fields
// Google can infer (city, district, street, postal code, building number) are
// SUGGESTED into the form — the customer checks and corrects them, and the
// National Short Address is still typed by the customer. Google is not a
// National Address verification and the page never says it is.
//
// Cost control: the map loads only when the customer opens it (one dynamic map
// load per opening), the search uses Places Autocomplete sessions, and a pin
// move costs one reverse geocode. The key comes from REACT_APP_GOOGLE_MAPS_API_KEY
// and must be restricted (HTTP referrers + these three APIs) with daily quotas
// in the Cloud console. Without a key the picker is not offered: manual entry.

export const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
export const mapsAvailable = () => !!MAPS_KEY || !!(typeof window !== 'undefined' && window.google?.maps);
const RIYADH = { lat: 24.7136, lng: 46.6753 };

let loading = null;
/** Load the Maps JavaScript API once (or use it if the page already has it). */
export function loadMaps(lang) {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (!MAPS_KEY) return Promise.reject(new Error('no_key'));
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const cb = `__maabarMaps${Date.now()}`;
      window[cb] = () => { delete window[cb]; resolve(window.google.maps); };
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(MAPS_KEY)}&libraries=places,geocoding&language=${lang === 'ar' ? 'ar' : 'en'}&region=SA&loading=async&callback=${cb}`;
      s.async = true;
      s.onerror = () => { loading = null; reject(new Error('load_failed')); };
      document.head.appendChild(s);
      setTimeout(() => reject(new Error('timeout')), 15000);
    });
  }
  return loading;
}

/** Google address components → our fields (suggestions only). Pure. */
export function fromGoogleComponents(components = []) {
  const get = (...types) => components.find((c) => types.some((t) => (c.types || []).includes(t)));
  const text = (c) => c?.long_name ?? c?.longText ?? '';
  const n = text(get('street_number'));
  return {
    city: text(get('locality')) || text(get('administrative_area_level_2')),
    district: text(get('sublocality_level_1', 'sublocality', 'neighborhood')),
    street: text(get('route')),
    postalCode: /^\d{5}$/.test(text(get('postal_code'))) ? text(get('postal_code')) : '',
    buildingNo: /^\d{4}$/.test(n) ? n : '',
  };
}

const T = {
  ar: { open: 'حدّد الموقع على الخريطة', search: 'ابحث عن الحي أو المعلم أو الشارع', loading: 'جارٍ تحميل الخريطة…', drag: 'اسحب الدبوس إلى مدخل المبنى',
    mine: 'استخدم موقعي', failed: 'تعذّر تحميل الخريطة — أكمل العنوان يدويًا.', outside: 'الموقع خارج المملكة — حرّك الدبوس.', filled: 'عبّأنا ما أمكن من الخريطة — راجعه وأكمل العنوان الوطني المختصر.', close: 'إخفاء الخريطة' },
  en: { open: 'Pick the location on the map', search: 'Search a district, landmark or street', loading: 'Loading the map…', drag: 'Drag the pin to the building entrance',
    mine: 'Use my location', failed: 'The map could not load — complete the address by hand.', outside: 'That point is outside Saudi Arabia — move the pin.', filled: 'We filled what the map knows — check it and add the National Short Address.', close: 'Hide the map' },
};
T.zh = T.en;

export default function MapPicker({ lang = 'ar', value, onPick }) {
  const c = T[lang] || T.ar;
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle');   // idle | loading | ready | failed
  const [note, setNote] = useState('');
  const mapEl = useRef(null), searchEl = useRef(null), marker = useRef(null), map = useRef(null), reverseRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setState('loading');
    loadMaps(lang).then((g) => {
      if (!alive || !mapEl.current) return;
      const start = inSaudiArabia(Number(value?.lat), Number(value?.lng)) ? { lat: Number(value.lat), lng: Number(value.lng) } : RIYADH;
      map.current = new g.Map(mapEl.current, {
        center: start, zoom: value?.lat ? 17 : 11, gestureHandling: 'greedy', streetViewControl: false, mapTypeControl: false, fullscreenControl: false,
        restriction: { latLngBounds: SA_BOUNDS, strictBounds: false },
      });
      marker.current = new g.Marker({ map: map.current, position: start, draggable: true });
      const reverse = (pos) => {
        const lat = pos.lat(), lng = pos.lng();
        if (!inSaudiArabia(lat, lng)) { setNote(c.outside); return; }
        new g.Geocoder().geocode({ location: { lat, lng } }).then(({ results }) => {
          onPick({ lat, lng, ...fromGoogleComponents(results?.[0]?.address_components) });
          setNote(c.filled);
        }).catch((e) => { onPick({ lat, lng }); console.warn('[MapPicker] reverse geocode failed:', e?.message || e); });
      };
      reverseRef.current = reverse;
      marker.current.addListener('dragend', () => reverse(marker.current.getPosition()));
      map.current.addListener('click', (e) => { marker.current.setPosition(e.latLng); reverse(e.latLng); });
      // Search: the Places (New) element, Saudi Arabia only.
      if (g.places?.PlaceAutocompleteElement && searchEl.current) {
        const ac = new g.places.PlaceAutocompleteElement({ includedRegionCodes: ['sa'] });
        ac.setAttribute('placeholder', c.search);
        searchEl.current.replaceChildren(ac);
        ac.addEventListener('gmp-select', async ({ placePrediction }) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['location', 'addressComponents'] });
          if (!place.location) return;
          map.current.setCenter(place.location); map.current.setZoom(17);
          marker.current.setPosition(place.location);
          reverse(place.location);
        });
      }
      setState('ready');
    }).catch((e) => { if (alive) { setState('failed'); console.warn('[MapPicker] maps unavailable:', e?.message || e); } });
    return () => { alive = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const mine = () => navigator.geolocation?.getCurrentPosition((p) => {
    const g = window.google?.maps;
    if (!g || !map.current) return;
    const ll = new g.LatLng(p.coords.latitude, p.coords.longitude);
    map.current.setCenter(ll); map.current.setZoom(17); marker.current.setPosition(ll);
    reverseRef.current?.(ll);
  });

  if (!mapsAvailable()) return null;
  if (!open) return <button type="button" className="mk-btn ghost mk-map-open" data-map-open="1" onClick={() => setOpen(true)}>{c.open}</button>;
  return (
    <div className="mk-map" data-map-state={state}>
      <div className="mk-map-tools">
        <div ref={searchEl} className="mk-map-search" data-map-search="1" />
        <button type="button" className="mk-btn ghost" onClick={mine} disabled={state !== 'ready'}>{c.mine}</button>
        <button type="button" className="mk-btn ghost" onClick={() => setOpen(false)}>{c.close}</button>
      </div>
      <div ref={mapEl} className="mk-map-canvas" data-map-canvas="1" />
      <p className="mk-count" data-map-note="1">{state === 'loading' ? c.loading : state === 'failed' ? c.failed : note || c.drag}</p>
    </div>
  );
}
